import { useEffect, useRef } from "react";
import { toast } from "sonner";

// Parse the currently-loaded frontend bundle hash from the <script> tag.
// CRA emits `/static/js/main.<hash>.js` at build time, so we can compare to
// what /api/version reports the server is serving right now.
const CURRENT_BUNDLE = (() => {
  try {
    const scripts = Array.from(document.querySelectorAll('script[src*="/static/js/main."]'));
    const src = scripts[0]?.src || "";
    const match = src.match(/main\.([a-f0-9]+)\.js/);
    return match?.[1] || "dev";
  } catch {
    return "dev";
  }
})();

// Polls /api/version and nudges the user to refresh if the server has shipped
// a new frontend bundle. Silent no-op in dev / when the server can't identify a bundle.
export default function VersionWatcher() {
  const nudgedRef = useRef(false);
  useEffect(() => {
    if (CURRENT_BUNDLE === "dev") return;

    const check = async () => {
      if (nudgedRef.current) return;
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/version`, { cache: "no-store" });
        if (!resp.ok) return;
        const data = await resp.json();
        const server = data.frontend_bundle;
        if (!server || server === "unknown" || server === "dev") return;
        if (server !== CURRENT_BUNDLE) {
          nudgedRef.current = true;
          toast.message("New version available", {
            description: "Tap refresh to get the latest Footymatch.",
            duration: Infinity,
            action: {
              label: "Refresh",
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        /* offline / transient — try again later */
      }
    };

    check();
    const id = setInterval(check, 5 * 60 * 1000); // every 5 minutes
    const onFocus = () => check(); // and whenever the user returns to the tab
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return null;
}
